import { FaBook, FaCodeBranch, FaEnvelope, FaMoon } from "react-icons/fa";
import EBoekhoudenStatusDot from "../../../components/eboekhouden/EBoekhoudenStatusDot";
import GiteaStatusDot from "../../../components/gitea/GiteaStatusDot";
import MailchimpStatusDot from "../../../components/mailchimp/MailchimpStatusDot";
import NoctisStatusDot from "../../../components/noctis/NoctisStatusDot";
import type { TFunction } from "i18next";
import IntegrationTile from "./IntegrationTile";

interface DashboardIntegrationsProps {
  visible: boolean;
  canSeeGiteaIssues: boolean;
  canSeeMailchimp: boolean;
  canSeeNoctis: boolean;
  canSeeEBoekhouden?: boolean;
  t: TFunction;
  onNavigate: (path: string) => void;
}

const DashboardIntegrations = ({ visible, canSeeGiteaIssues, canSeeMailchimp, canSeeNoctis, canSeeEBoekhouden = false, t, onNavigate }: DashboardIntegrationsProps) => {
  if (!visible) return null;
  const badge = t("dashboard.integrations.badge", { defaultValue: "Integration" });

  return (
    <section className="dashboard-enter space-y-4 [animation-delay:180ms]">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          {t("dashboard.sections.integrations.title", { defaultValue: "Integrations" })}
        </h3>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {t("dashboard.sections.integrations.description", {
            defaultValue: "Gitea, Mailchimp, Noctis and e-Boekhouden integrations.",
          })}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {canSeeGiteaIssues ? (
          <IntegrationTile icon={<FaCodeBranch className="text-3xl text-emerald-700 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-emerald-200" />} title="Gitea" status={<GiteaStatusDot showLabel={false} />} description={t("dashboard.gitea.desc", { defaultValue: "Issues & workflow status" })} badge={badge} onClick={() => onNavigate("/gitea")} />
        ) : null}
        {canSeeMailchimp ? (
          <IntegrationTile icon={<FaEnvelope className="text-3xl text-emerald-700 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-emerald-200" />} title="Mailchimp" status={<MailchimpStatusDot showLabel={false} />} description={t("dashboard.mailchimp.desc", { defaultValue: "Sync status & configuration" })} badge={badge} onClick={() => onNavigate("/integrations/mailchimp")} />
        ) : null}
        {canSeeNoctis ? (
          <IntegrationTile icon={<FaMoon className="text-3xl text-emerald-700 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-emerald-200" />} title="Noctis" status={<NoctisStatusDot showLabel={false} />} description={t("dashboard.noctis.desc", { defaultValue: "Connection status & queue" })} badge={badge} onClick={() => onNavigate("/noctis")} />
        ) : null}
        {canSeeEBoekhouden ? (
          <IntegrationTile
            icon={<FaBook className="text-3xl text-emerald-700 transition duration-200 group-hover:scale-105 motion-reduce:transform-none dark:text-emerald-200" />}
            title="e-Boekhouden"
            description={t("dashboard.eboekhouden.desc", { defaultValue: "Payments for the parent contribution" })}
            badge={badge}
            status={<EBoekhoudenStatusDot showLabel={false} />}
            onClick={() => onNavigate("/integrations/eboekhouden")}
          />
        ) : null}
      </div>
    </section>
  );
};

export default DashboardIntegrations;
